import React, { useState } from 'react';
import styled from 'styled-components';
import PageBanner from '../components/common/PageBanner';
import ContentSection from '../components/common/ContentSection';
import SectionTitle from '../components/common/SectionTitle';
import Button from '../components/common/Button';
import { FaCalendar, FaUserAlt, FaArrowRight, FaDownload } from 'react-icons/fa';
import { theme } from '../styles/GlobalStyles';

const TabsContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
  border-bottom: 1px solid ${theme.border};
  gap: 0.25rem;
`;

const Tab = styled.div`
  padding: 0.85rem 1.75rem;
  font-size: 1rem;
  font-weight: 500;
  color: ${props => props.active ? '#615DFF' : theme.textMuted};
  border-bottom: 2px solid ${props => props.active ? '#615DFF' : 'transparent'};
  cursor: pointer;
  transition: all 0.25s ease;
  margin-bottom: -1px;

  &:hover { color: ${theme.text}; }

  @media (max-width: 768px) { padding: 0.75rem 1.25rem; font-size: 0.9rem; }
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 1.5rem;
  margin-top: 2.5rem;
`;

const BlogCard = styled.div`
  background: ${theme.cardBg};
  border: 1px solid ${theme.border};
  border-radius: 16px;
  overflow: hidden;
  backdrop-filter: blur(12px);
  box-shadow: ${theme.cardShadow};
  transition: all 0.3s ease;

  &:hover {
    background: ${theme.cardBgHover};
    border-color: rgba(97,93,255,0.3);
    transform: translateY(-6px);
    box-shadow: 0 16px 48px rgba(97,93,255,0.15);
  }
`;

const BlogImage = styled.div`
  height: 190px;
  background-size: cover;
  background-position: center;
`;

const BlogContent = styled.div`
  padding: 1.5rem;
`;

const BlogCategory = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 100px;
  background: rgba(97,93,255,0.1);
  border: 1px solid rgba(97,93,255,0.2);
  color: #a5a2ff;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 0.75rem;
`;

const BlogTitle = styled.h3`
  font-size: 1.1rem;
  margin-bottom: 0.75rem;
  font-weight: 600;

  a {
    color: ${theme.text};
    text-decoration: none;
    transition: color 0.2s;
    &:hover { color: #a5a2ff; }
  }
`;

const BlogMeta = styled.div`
  display: flex;
  gap: 1.25rem;
  color: ${theme.textDim};
  margin-bottom: 0.75rem;
  font-size: 0.8rem;

  span {
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }
`;

const BlogExcerpt = styled.p`
  font-size: 0.875rem;
  color: ${theme.textMuted};
  margin-bottom: 1.25rem;
  line-height: 1.65;
`;

const ReadMore = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.82rem;
  font-weight: 600;
  background: ${theme.gradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  transition: gap 0.2s;
  cursor: pointer;
  &:hover { gap: 0.6rem; }
`;

/* Whitepapers */
const WhitepaperGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 1.5rem;
  margin-top: 2.5rem;
`;

const WhitepaperCard = styled.div`
  background: ${theme.cardBg};
  border: 1px solid ${theme.border};
  border-radius: 16px;
  padding: 2rem;
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
  backdrop-filter: blur(12px);
  box-shadow: ${theme.cardShadow};
  transition: all 0.3s ease;

  &:hover {
    background: ${theme.cardBgHover};
    border-color: rgba(97,93,255,0.3);
    transform: translateY(-4px);
  }
`;

const WhitepaperIcon = styled.div`
  width: 50px; height: 50px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(97,93,255,0.2), rgba(61,217,235,0.1));
  border: 1px solid rgba(97,93,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #615DFF;
  font-size: 1.3rem;
  flex-shrink: 0;
`;

const WhitepaperInfo = styled.div`
  flex: 1;
  h3 { font-size: 1.05rem; color: ${theme.text}; margin-bottom: 0.5rem; }
  p  { font-size: 0.875rem; color: ${theme.textMuted}; margin-bottom: 1rem; line-height: 1.6; }
`;

const CaseStudyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 2.5rem;
`;

const CaseStudyCard = styled.div`
  background: ${theme.cardBg};
  border: 1px solid ${theme.border};
  border-radius: 16px;
  overflow: hidden;
  backdrop-filter: blur(12px);
  box-shadow: ${theme.cardShadow};
  transition: all 0.3s ease;

  &:hover {
    background: ${theme.cardBgHover};
    border-color: rgba(97,93,255,0.3);
    transform: translateY(-6px);
  }
`;

const CaseStudyImage = styled.div`
  height: 160px;
  background-size: cover;
  background-position: center;
`;

const CaseStudyContent = styled.div`
  padding: 1.5rem;
  h3 { font-size: 1.05rem; color: ${theme.text}; margin-bottom: 0.5rem; }
  p  { font-size: 0.875rem; color: ${theme.textMuted}; margin-bottom: 1rem; }
`;

const StatRow = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
`;

const Stat = styled.div`
  strong {
    display: block;
    font-size: 1.3rem;
    background: ${theme.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 2px;
  }
  span { font-size: 0.75rem; color: ${theme.textDim}; }
`;

const blogPosts = [
  { category: 'Counterfeiting', title: 'The $4.5 Trillion Problem: Why Product Authentication Matters More Than Ever', date: 'March 15, 2025', author: 'Sarah Johnson', excerpt: 'Global counterfeiting costs brands and economies trillions annually. Discover how technology is changing the fight against fake products.', img: 'https://images.unsplash.com/photo-1601597111158-2fceff292cdc?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80' },
  { category: 'Product Security', title: "How QR Codes are Revolutionising Product Authentication", date: 'February 28, 2025', author: 'Michael Chen', excerpt: 'Modern QR code technology goes far beyond simple links. Learn how Prodseal uses advanced encryption to make unforgeable product codes.', img: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80' },
  { category: 'Brand Protection', title: '5 Ways Product Authentication Builds Consumer Trust', date: 'February 10, 2025', author: 'Emma Williams', excerpt: 'Trust is the foundation of every purchase. Explore five concrete ways authentication technology helps brands build lasting consumer loyalty.', img: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80' },
  { category: 'Industry Insights', title: 'Pharmaceuticals and Counterfeiting: A Life-or-Death Problem', date: 'January 22, 2025', author: 'Dr. James Park', excerpt: "Counterfeit drugs are dangerous and prevalent. Understand the pharmaceutical industry's unique authentication challenges and solutions.", img: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80' },
  { category: 'Technology', title: 'Digital Warranties: The Future of Customer Protection', date: 'January 8, 2025', author: 'Sarah Johnson', excerpt: 'Paper warranty cards are becoming obsolete. Discover how digital warranties offer better protection for consumers and valuable data for brands.', img: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80' },
  { category: 'Case Studies', title: 'How a Luxury Brand Reduced Counterfeiting by 78% in One Year', date: 'December 15, 2024', author: 'Michael Chen', excerpt: 'Explore this detailed case study of how a major luxury goods brand implemented Prodseal and dramatically reduced counterfeit products in their market.', img: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80' },
];

const whitepapers = [
  { title: 'The Complete Guide to Product Authentication for FMCG Brands', desc: 'A comprehensive guide to implementing product authentication in the fast-moving consumer goods industry.' },
  { title: 'Fighting Pharmaceutical Counterfeiting: Strategies and Technologies', desc: 'An in-depth exploration of authentication solutions specifically designed for the pharmaceutical sector.' },
  { title: 'ROI of Brand Protection: Measuring the Business Value of Anti-Counterfeiting', desc: 'How to calculate and communicate the return on investment of implementing product authentication.' },
  { title: "Digital Warranty Management: The Brand's Complete Guide", desc: 'Everything brands need to know about transitioning from paper to digital warranty programs.' },
];

const caseStudies = [
  { title: 'Luxury Fashion Brand Reduces Counterfeiting by 78%', industry: 'Fashion & Luxury', results: { v1: '78%', l1: 'Counterfeit reduction', v2: '42%', l2: 'Customer engagement lift' }, img: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80' },
  { title: 'Pharma Company Achieves Full Supply Chain Visibility', industry: 'Pharmaceuticals', results: { v1: '100%', l1: 'Track & trace coverage', v2: '63%', l2: 'Faster recall response' }, img: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80' },
  { title: 'Electronics Giant Slashes Warranty Fraud by 91%', industry: 'Electronics', results: { v1: '91%', l1: 'Warranty fraud reduction', v2: '3.2×', l2: 'Activation rate improvement' }, img: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80' },
];

const TABS = ['Blog', 'Whitepapers', 'Case Studies'];

const ResourcesPage = () => {
  const [activeTab, setActiveTab] = useState('Blog');

  return (
    <>
      <PageBanner
        title="Resources"
        subtitle="Insights, case studies, and research to help you protect your brand and build consumer trust."
      />
      <ContentSection>
        <TabsContainer>
          {TABS.map((t) => (
            <Tab key={t} active={activeTab === t} onClick={() => setActiveTab(t)}>{t}</Tab>
          ))}
        </TabsContainer>

        {activeTab === 'Blog' && (
          <BlogGrid>
            {blogPosts.map((post) => (
              <BlogCard key={post.title}>
                <BlogImage style={{ backgroundImage: `url('${post.img}')` }} />
                <BlogContent>
                  <BlogCategory>{post.category}</BlogCategory>
                  <BlogTitle><span>{post.title}</span></BlogTitle>
                  <BlogMeta>
                    <span><FaCalendar />{post.date}</span>
                    <span><FaUserAlt />{post.author}</span>
                  </BlogMeta>
                  <BlogExcerpt>{post.excerpt}</BlogExcerpt>
                  <ReadMore>Read More <FaArrowRight size={11} /></ReadMore>
                </BlogContent>
              </BlogCard>
            ))}
          </BlogGrid>
        )}

        {activeTab === 'Whitepapers' && (
          <>
            <SectionTitle>Research & Whitepapers</SectionTitle>
            <WhitepaperGrid>
              {whitepapers.map((wp) => (
                <WhitepaperCard key={wp.title}>
                  <WhitepaperIcon><FaDownload /></WhitepaperIcon>
                  <WhitepaperInfo>
                    <h3>{wp.title}</h3>
                    <p>{wp.desc}</p>
                    <Button primary>Download PDF</Button>
                  </WhitepaperInfo>
                </WhitepaperCard>
              ))}
            </WhitepaperGrid>
          </>
        )}

        {activeTab === 'Case Studies' && (
          <>
            <SectionTitle>Customer Case Studies</SectionTitle>
            <CaseStudyGrid>
              {caseStudies.map((cs) => (
                <CaseStudyCard key={cs.title}>
                  <CaseStudyImage style={{ backgroundImage: `url('${cs.img}')` }} />
                  <CaseStudyContent>
                    <BlogCategory>{cs.industry}</BlogCategory>
                    <h3>{cs.title}</h3>
                    <StatRow>
                      <Stat><strong>{cs.results.v1}</strong><span>{cs.results.l1}</span></Stat>
                      <Stat><strong>{cs.results.v2}</strong><span>{cs.results.l2}</span></Stat>
                    </StatRow>
                    <p style={{ marginTop: '1rem' }}><ReadMore>Read Case Study <FaArrowRight size={11} /></ReadMore></p>
                  </CaseStudyContent>
                </CaseStudyCard>
              ))}
            </CaseStudyGrid>
          </>
        )}
      </ContentSection>
    </>
  );
};

export default ResourcesPage;
